import { useState, useEffect, useCallback } from 'react';
import { plansApi, Plan, CreatePlanDto } from '../../../api/payments';
import { toast } from 'sonner';
import { UsePlansReturn } from '../types';

const initialFormData: CreatePlanDto = {
  code: '',
  title: '',
  duration_value: 1,
  duration_unit: 'month',
  price: 0,
  currency: 'UZS',
  is_active: true,
};

export function usePlans(): UsePlansReturn {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // На админке по умолчанию видим все, включая неактивные.
  const [includeInactive, setIncludeInactive] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [formData, setFormData] = useState<CreatePlanDto>(initialFormData);

  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await plansApi.getAll(includeInactive);
      setPlans(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch plans');
    } finally {
      setIsLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleCreate = async () => {
    try {
      await plansApi.create(formData);
      toast.success('Plan created successfully');
      setIsCreateDialogOpen(false);
      setFormData(initialFormData);
      fetchPlans();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create plan');
    }
  };

  const handleEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      code: plan.code,
      title: plan.title,
      duration_value: plan.duration_value,
      duration_unit: plan.duration_unit,
      price: plan.price,
      old_price: plan.old_price || undefined,
      currency: plan.currency,
      is_active: plan.is_active,
      metadata: plan.metadata || undefined,
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingPlan) return;
    try {
      await plansApi.update(editingPlan.id, formData);
      toast.success('Plan updated successfully');
      setIsEditDialogOpen(false);
      setEditingPlan(null);
      fetchPlans();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update plan');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this plan?')) return;
    try {
      await plansApi.delete(id);
      toast.success('Plan deleted successfully');
      fetchPlans();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete plan');
    }
  };

  // Optimistic toggle is_active: сразу обновляем UI, при ошибке откатываем.
  const handleToggleActive = async (plan: Plan) => {
    const next = !plan.is_active;
    setPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, is_active: next } : p)));
    try {
      await plansApi.update(plan.id, { is_active: next });
      toast.success(next ? 'Plan activated' : 'Plan deactivated');
    } catch (error: any) {
      setPlans((prev) =>
        prev.map((p) => (p.id === plan.id ? { ...p, is_active: plan.is_active } : p)),
      );
      toast.error(error.response?.data?.message || 'Failed to update plan status');
    }
  };

  return {
    plans,
    isLoading,
    includeInactive,
    isCreateDialogOpen,
    isEditDialogOpen,
    editingPlan,
    formData,
    setIncludeInactive,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    handleToggleActive,
  };
}
